package com.macrofocus.eflux.network

import com.macrofocus.eflux.ViewStyle
import com.macrofocus.eflux.network.logical.LogicalBranchId
import com.macrofocus.eflux.network.logical.LogicalBusId
import com.macrofocus.eflux.network.logical.logicalBranchIdOf
import com.macrofocus.eflux.network.logical.logicalBusIdOf
import com.macrofocus.eflux.ui.PaletteType
import com.macrofocus.eflux.ui.svg.dataName
import js.array.asList
import org.molap.dataframe.DataFrame
import org.molap.dataframe.reindexRowsUsingColumnDefault
import org.molap.network.AbstractNetwork
import org.molap.network.DirectedLink
import web.cssom.ClassName
import web.dom.Element
import web.dom.ElementId
import web.geometry.DOMPoint
import web.geometry.DOMRect
import web.svg.*

class GraphicalNetwork(val svg: SVGSVGElement, itcmDataFrame: DataFrame<Int, String, Any?>) : AbstractNetwork<Point, Path>() {
    private val DEBUG = false

    val points: Map<LogicalBusId?, Point>
    val paths: MutableMap<LogicalBranchId, Path>

    val pointsParent = svg.getElementById(ElementId("US")) as SVGGraphicsElement
    val dreibeine = svg.getElementById(ElementId("Dreibeine")) as SVGGraphicsElement
    val pathsParent = svg.getElementById(ElementId("Netz_Standard")) as SVGGraphicsElement
    val legendeParent = svg.getElementById(ElementId("Legende")) as SVGGraphicsElement
    val lodParent = svg.ownerDocument.createElementNS("http://www.w3.org/2000/svg", "g") as SVGElement

    val CHECK_CONNECTED = false

    init {
        // Adding LoD group
        pointsParent.parentElement?.append(lodParent)
        lodParent.id = ElementId("lod")
        lodParent.style.visibility = "hidden"

        paths = mutableMapOf()

        if(DEBUG) println("Creating nodesElements")
        val nodesElements = pointsParent.children.asList().filterIsInstance<SVGElement>()
            .union(dreibeine.children.asList().filterIsInstance<SVGElement>())
            .toList()
        val pathElements = pathsParent.children.asList().filterIsInstance<SVGElement>()

        if(DEBUG) println("Adding nodes")
        points = groupPoints(nodesElements)
        points.forEach { addNode(it.value) }

        if(DEBUG) println("Adding " + points.size + " graphical nodes")

//        addBranches(pathElements, itcmDataFrame)
        try {
            fixBranches(pathElements)
        } catch (e: Exception) {
            throw RuntimeException("Failed to fix branches", e)
        }

        val itcmNames = itcmDataFrame.rows()
            .associateBy(
                { logicalBranchIdOf(itcmDataFrame.getValueAt(it, "raw_busbar_name_left7_i-raw_busbar_name_left7_j-ckt") as String) },
                { itcmDataFrame.getValueAt(it, "FullName ITCM") as String? })

        if(DEBUG) println("Populating paths")
        for (element in pathElements) {
            val id = element.dataName

            if (id.isNotBlank()) {
                val ids = id.split("-")

                if (ids.size >= 2) {
                    val from = logicalBusIdOf(ids[0])
                    val to = logicalBusIdOf(ids[1])

                    val key = logicalBranchIdOf(id)
                    if (paths.containsKey(key)) {
                        paths[key]!!.addElement(element)
                    } else {
                        val longName = itcmNames.get(key)
                        if (longName == null) {
                            println("Couldn't find ITCM name for $key")
                        }
                        val value = Path(key, longName, element)
                        paths[key] = value
                        if (!CHECK_CONNECTED || (points.contains(from) && points.contains(to))) {
                            val f = points[from]
                            val t = points[to]

                            if(f != null && t != null) {
                                val p1 = f
                                val p2 = t

                                addUndirectedEdge(p1, p2, value)
                            } else {
                                println("Missing from and to for edge $from -> $to (${value.id})")
                                element.classList.add(ClassName("link-inactive"))
                            }

                        } else {
                            // Special case for line from Bassecourt
                            if (id == "SBASSE2-SMUEH32-1") {
                                val p1 = points[from]!!
                                val p2 = points[logicalBusIdOf("SMUEHB2")]!!

                                addUndirectedEdge(p1, p2, value)
                            } else {
                                println("Invalid branch $key for $from to $to")
                                element.classList.add(ClassName("link-inactive"))
                            }
                        }
                    }

//                    element.style.setProperty("stroke", "orange")
//                    element.style.setProperty("stroke-width", "1.2")

//                    val titleElement = document.createElementNS("http://www.w3.org/2000/svg", "title")
//                    with(titleElement) {
//                        textContent = from.name + "-" + to.name
//                    }
//                    element.appendChild(titleElement)
                } else {
                    element.classList.add(ClassName("link-invalid"))
                    println("Invalid id " + id + " for " + element + ": " + element.tagName + " " + element.className)
                }
            } else {
                element.classList.add(ClassName("link-invalid"))
            }
        }

        if(DEBUG) println("Adding " + paths.size + " graphicals links")
    }

    /**
     * Update the colors using in the SVG graphics according to the theme colors.
     */
    fun applyThemeColors(
        paletteType: PaletteType,
        background: ClassName,
        country: ClassName,
        cities: Boolean,
        labelSubstations: Boolean,
        border: Boolean,
        legend: Boolean,
        viewStyle: ViewStyle
    ) {
        val root = svg.getElementById(ElementId("Dunkel_blau"))
        if (root is SVGGraphicsElement) {
            if (paletteType == PaletteType.dark) {
                root.classList.add(ClassName("dark"))
                root.classList.remove(ClassName("light"))
            } else {
                root.classList.add(ClassName("light"))
                root.classList.remove(ClassName("dark"))
            }

            if (viewStyle == ViewStyle.VOLTAGE) {
                root.classList.add(ClassName("voltage"))
                root.classList.remove(ClassName("flow"))
            } else {
                root.classList.add(ClassName("flow"))
                root.classList.remove(ClassName("voltage"))
            }
        }

        val countryLayer = svg.getElementById(ElementId("Schweiz"))
        if (countryLayer is SVGGraphicsElement) {
            val backgroundElement = countryLayer.children[0]
            val countryElement = countryLayer.children[1]
            if (backgroundElement is SVGElement) {
                backgroundElement.classList.value = background
            } else {
                println("Unknown element $backgroundElement")
            }
            if (countryElement is SVGElement) {
                countryElement.style.visibility = if (border) "visible" else "hidden"
                countryElement.classList.value = country
            } else {
                println("Unknown element $countryElement")
            }
        } else {
            println("Could not locate expected element " + "Schweiz")
        }

        val citiesLayer = svg.getElementById(ElementId("cities"))
        if (citiesLayer is SVGGraphicsElement) {
            citiesLayer.style.visibility = if (cities) "visible" else "hidden"
        } else {
            println("Could not locate expected element " + "cities")
        }
        val labelSubstationsLayer = svg.getElementById(ElementId("label_substations"))
        if (labelSubstationsLayer is SVGGraphicsElement) {
            labelSubstationsLayer.style.visibility = if (labelSubstations) "visible" else "hidden"
        } else {
            println("Could not locate expected element " + "label_substations")
        }

        val legendLayer = svg.getElementById(ElementId("Legende"))
        if (legendLayer is SVGGraphicsElement) {
            legendLayer.style.visibility = if (legend) "visible" else "hidden"
        }
    }

    private fun groupPoints(pointElements: List<Element>): Map<LogicalBusId?, Point> =
        pointElements
            .filterIsInstance<SVGGraphicsElement>()
            .groupBy {
                logicalBusIdOf(it.dataName)
            }
            .filter {
                val notBlank = it.key.name.isNotBlank()

                if (notBlank) {
                    // Highlight nodes that have been found
//                    for (element in it.value) {
//                        element.style.setProperty("stroke", "orange")
//                        element.style.setProperty("fill", "red")
//                        element.style.animationName = "blinking-effect"
//                        element.style.animationDuration = "0.5s"
//                        element.style.animationIterationCount = "infinite"
//                        element.style.animationTimingFunction = "ease-in-out"
//                        element.style.animationDirection = "alternate"
//                    }
                } else {
                    // Grey out nodes that have been found
//                    for (element in it.value) {
//                        element.style.setProperty("stroke", "#88888840")
//                    }
                }

                notBlank
            }
            .mapValues { Point(it.key!!, it.value) }

    private fun addBranches(pathElements: List<Element>, itcmDataFrame: DataFrame<Int, String?, Any?>) {
        val labeledPaths = pathElements
            .filterIsInstance<SVGGeometryElement>()
            .filter { it.dataName.isBlank() }

        val indexedCoordinatesDataFrame by lazy {
            itcmDataFrame.reindexRowsUsingColumnDefault(
                true,
                "raw_busbar_name_left7_i-raw_busbar_name_left7_j-ckt"
            )
        }

        for (labeledPath in labeledPaths) {
            val computeLink: DirectedLink<Point, Point>? = generateLink(listOf(labeledPath))
//            val oldName = labeledPath.key.from.id.name + "-" + labeledPath.key.to.id.name
            if (computeLink == null) {
//                println("Missing " + oldName)
                //                println(labeledPath.key.toString() + " is null")
            } else {
                //                println(labeledPath.key.toString() + " -> " + computeLink)
//                val label = Path()
//                for(i in labeledPath) {
//                    label.addElement(i)
                var id: String? = computeLink.from.id.name + "-" + computeLink.to.id.name + "-1"
                if (!indexedCoordinatesDataFrame.rowIndex.contains(id)) {
                    id = computeLink.to.id.name + "-" + computeLink.from.id.name + "-1"
                    if (!indexedCoordinatesDataFrame.rowIndex.contains(id)) {
                        id = null
                    }
                }

                if (id != null) {
                    labeledPath.setAttribute("data-name", id)
                    labeledPath.style.setProperty("stroke-width", "1.4px")
                }
//                }
//                addUndirectedEdge(computeLink.from, computeLink.to, label)
                //                if (computeLink != labeledPath.key) {
//                    val newName = computeLink.from.id.name + "-" + computeLink.to.id.name
//                    if (computeLink.from == labeledPath.key.to && computeLink.to == labeledPath.key.from) {
//                        println("Inversed: " + oldName + " -> " + newName)
//                        for (v in labeledPath.value) {
//                            v.setAttribute("data-name", newName)
//                        }
//                    } else {
//                        println("Mismatched: " + oldName + " -> " + newName)
//                    }
//                }
            }
        }
    }

    private fun fixBranches(pathElements: List<Element>) {
        val labeledPaths = pathElements
            .filterIsInstance<SVGElement>()
            .groupBy {
                val id = it.dataName
                val ids = id.split("-")
                if (ids.size >= 2) {
                    val from = logicalBusIdOf(ids[0])
                    val to = logicalBusIdOf(ids[1])

                    if (points.contains(from) && points.contains(to)) {
                        val p1 = points[from]!!
                        val p2 = points[to]!!
                        Pair(DirectedLink(p1, p2), ids[2])
                    } else {
                        null
                    }
                } else {
                    null
                }
            }
            .filter { it.key != null }
            .mapKeys { it.key!! }

        for (labeledPath in labeledPaths) {
            val computeLink: DirectedLink<Point, Point>? = computeLink(labeledPath.key.first, labeledPath.value)
            val oldName =
                labeledPath.key.first.from.id.name + "-" + labeledPath.key.first.to.id.name + "-" + labeledPath.key.second
            if (computeLink == null) {
                println("Missing $oldName")
                //                println(labeledPath.key.toString() + " is null")
            } else {
                //                println(labeledPath.key.toString() + " -> " + computeLink)
                if (computeLink != labeledPath.key.first) {
                    val newName = computeLink.from.id.name + "-" + computeLink.to.id.name + "-" + labeledPath.key.second
                    if (computeLink.from == labeledPath.key.first.to && computeLink.to == labeledPath.key.first.from) {
                        println("Inversed: $oldName -> $newName")
                        for (v in labeledPath.value) {
                            v.setAttribute("data-name", newName)
                        }
                    } else {
                        println("Mismatched: $oldName -> $newName")
                    }
                }
            }
        }
    }

    private fun generateLink(
        paths: List<SVGElement>
    ): DirectedLink<Point, Point>? {
        for (element in paths) {
            if (element is SVGPathElement) {
                var start: Point? = null
                var end: Point? = null
                for (point in nodes) {
                    if (intersectStart(point, element)) {
                        start = point
                        break
                    }
                }
                for (point in nodes) {
                    if (start != point && intersectEnd(point, element)) {
                        end = point
                        break
                    }
                }
                if (start != null && end != null) {
                    return DirectedLink(start, end)
                }
            } else if (element is SVGLineElement) {
                var start: Point? = null
                var end: Point? = null
                for (point in nodes) {
                    if (intersectStart(point, element)) {
                        start = point
                        break
                    }
                }
                for (point in nodes) {
                    if (start != point && intersectEnd(point, element)) {
                        end = point
                        break
                    }
                }
                if (start != null && end != null) {
                    return DirectedLink(start, end)
                }
            } else if (element is SVGPolylineElement) {
                var start: Point? = null
                var end: Point? = null
                for (point in nodes) {
                    if (intersectStart(point, element)) {
                        start = point
                        break
                    }
                }
                for (point in nodes) {
                    if (start != point && intersectEnd(point, element)) {
                        end = point
                        break
                    }
                }
                if (start != null && end != null) {
                    return DirectedLink(start, end)
                }
            } else {
//                println("Unknown element type for " + element + ": " + element.tagName + " " + element.className)
                return generateLink(element.children.asList().map { it as SVGElement })
            }
        }
        return null
    }

    private fun computeLink(
        link: DirectedLink<Point, Point>,
        paths: List<SVGElement>
    ): DirectedLink<Point, Point>? {
//        if(paths.size > 1) {
//            println("Warning: link " + link + " contains multiple paths " + paths.size)
//        }
        for (element in paths) {
            if (element is SVGPathElement) {
//                println("SVGPathElement " + element)
                val start = intersectStart(link.from, element)
                val end = intersectEnd(link.to, element)
//                println("" + link + ": " + start + ", " + end)
                if (start && end) {
                    return DirectedLink(link.from, link.to)
                } else {
                    val rstart = intersectStart(link.to, element)
                    val rend = intersectEnd(link.from, element)
                    if (rstart && rend) {
                        return DirectedLink(link.to, link.from)
                    }
                }
            } else if (element is SVGLineElement) {
                var computedLink: DirectedLink<Point, Point>? = null
                if (intersectStart(link.from, element) && intersectEnd(link.to, element)) {
                    computedLink = DirectedLink(link.from, link.to)
                } else {
                    if (intersectStart(link.to, element) && intersectEnd(link.from, element)) {
                        computedLink = DirectedLink(link.to, link.from)
                    }
                }
//                if(computedLink == null) {
//                    println("Cannot computing link between " + link.from + " and " + link.to + " with element " + element.tagName)
//                }
                return computedLink
            } else if (element is SVGPolylineElement) {
                val start = intersectStart(link.from, element)
                val end = intersectEnd(link.to, element)
//                println("" + link + ": " + start + ", " + end)
                if (start && end) {
                    return DirectedLink(link.from, link.to)
                } else {
                    val rstart = intersectStart(link.to, element)
                    val rend = intersectEnd(link.from, element)
                    if (rstart && rend) {
                        return DirectedLink(link.to, link.from)
                    }
                }
//                println("SVGPolyLineElement " + element)
            } else {
//                println("Unknown element type for " + element + ": " + element.tagName + " " + element.className)
                return computeLinkForGroup(link, element.children.asList().map { it as SVGElement })
            }
        }
        return null
    }

    private fun computeLinkForGroup(
        link: DirectedLink<Point, Point>,
        paths: List<SVGElement>
    ): DirectedLink<Point, Point>? {
        var from: Point? = null
        var to: Point? = null

        for (element in paths) {
            if (element is SVGPathElement) {
//                println("SVGPathElement " + element)
                if (intersectStart(link.from, element)) {
                    from = link.from
                }
                if (intersectEnd(link.to, element)) {
                    to = link.to
                }
                if (intersectStart(link.to, element)) {
                    from = link.to
                }
                if (intersectEnd(link.from, element)) {
                    to = link.from
                }
            } else if (element is SVGLineElement) {
//                println("SVGLineElement " + element)
                if (intersectStart(link.from, element)) {
                    from = link.from
                }
                if (intersectEnd(link.to, element)) {
                    to = link.to
                }
                if (intersectStart(link.to, element)) {
                    from = link.to
                }
                if (intersectEnd(link.from, element)) {
                    to = link.from
                }
            } else if (element is SVGPolylineElement) {
                if (intersectStart(link.from, element)) {
                    from = link.from
                }
                if (intersectEnd(link.to, element)) {
                    to = link.to
                }
                if (intersectStart(link.to, element)) {
                    from = link.to
                }
                if (intersectEnd(link.from, element)) {
                    to = link.from
                }
            } else {
                println("Unknown element type for " + element + ": " + element.tagName + " " + element.className)
//                return computeLink(link, element.children.asList().map { it as SVGElement })
            }
        }
        return if (from != null && to != null) {
            DirectedLink(from, to)
        } else {
            null
        }
    }

    fun verify() {

    }

    fun intersection(point: Point, paths: List<SVGElement>) : Double?{
        for (element in paths) {
            if (intersectStart(point, element)) {
                return 1.0
            }
            if (intersectEnd(point, element)) {
                return 1.0
            }
        }
        return null;
    }

    fun intersectStart(point: Point, element: SVGElement): Boolean {
        if (element is SVGPathElement) {
            return intersectStart(point, element)
        } else if (element is SVGLineElement) {
            return intersectStart(point, element)
        } else if (element is SVGPolylineElement) {
            return intersectStart(point, element)
        } else {
            println("intersectStart(): Unknown element type for " + element + ": " + element.tagName + " " + element.className)
        }
        return false
    }

    fun intersectEnd(point: Point, element: SVGElement): Boolean {
        if (element is SVGPathElement) {
            return intersectEnd(point, element)
        } else if (element is SVGLineElement) {
            return intersectEnd(point, element)
        } else if (element is SVGPolylineElement) {
            return intersectEnd(point, element)
        } else {
            println("intersectEnd(): Unknown element type for " + element + ": " + element.tagName + " " + element.className)
        }
        return false
    }

    fun intersectStart(point: Point, line: SVGPolylineElement): Boolean {
//        svg.checkIntersection()
        val lineExtremity = line.points.asDynamic()[0]
        val intersectRect = intersectRect(point, lineExtremity)
        return intersectRect
    }

    fun intersectEnd(point: Point, line: SVGPolylineElement): Boolean {
//        svg.checkIntersection()
        val lineExtremity = line.points.asDynamic()[line.points.length - 1]
        val intersectRect = intersectRect(point, lineExtremity)
        return intersectRect
    }

    fun intersectStart(point: Point, line: SVGGeometryElement): Boolean {
//        svg.checkIntersection()
        val lineExtremity = line.getPointAtLength(0f)
        val intersectRect = intersectRect(point, lineExtremity)
        return intersectRect
    }

    fun intersectEnd(point: Point, line: SVGGeometryElement): Boolean {
//        svg.checkIntersection()
        val lineExtremity = line.getPointAtLength(line.getTotalLength())
        return intersectRect(point, lineExtremity)
    }

    fun intersectRect(point: Point, x: Float, y: Float): Boolean {
        for (element in point.elements) {
            val r2 = element.getBoundingClientRect()
//            println("" + r2.left + "," + r2.top + "," + r2.right + "," + r2.bottom + ", " + x + ", " + y)
//            if(element.isPointInFill(DOMPoint()))
            if (intersectRect(r2, x, y)) {
                return true
            }
        }
        return false
    }

    fun intersectRect(point: Point, x: Double, y: Double): Boolean {
        for (element in point.elements) {
            val r2 = element.getBoundingClientRect()
//            println("" + r2.left + "," + r2.top + "," + r2.right + "," + r2.bottom + ", " + x + ", " + y)
//            if(element.isPointInFill(DOMPoint()))
            if (intersectRect(r2, x, y)) {
                return true
            }
        }
        return false
    }

    fun intersectRect(point: Point, p: DOMPoint): Boolean {
        for (element in point.elements) {
            if(element is SVGGeometryElement) {
                if (element.isPointInStroke(p) || element.isPointInFill(p)) {
                    return true
                }
            } else {
                println("Element $element is not of type SVGGeometryElement")
            }
        }
        return false
    }

    fun intersectRect(r2: DOMRect, x: Double, y: Double): Boolean {
        return r2.left <= x && x <= r2.right &&
                r2.top <= y && y <= r2.bottom
    }

    fun intersectRect(r2: DOMRect, x: Float, y: Float): Boolean {
        return r2.left <= x && x <= r2.right &&
                r2.top <= y && y <= r2.bottom
    }

    fun intersectRect(r1: SVGRectElement, r2: SVGRectElement): Boolean {
        val bbr1 = r1.getBoundingClientRect()    //BOUNDING BOX OF THE FIRST OBJECT
        val bbr2 = r2.getBoundingClientRect()    //BOUNDING BOX OF THE SECOND OBJECT

        //CHECK IF THE TWO BOUNDING BOXES OVERLAP
        return !(bbr2.left > bbr1.right ||
                bbr2.right < bbr1.left ||
                bbr2.top > bbr1.bottom ||
                bbr2.bottom < bbr1.top)
    }
}